import { useState, type FormEvent } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ArrowLeft, Save } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props extends Omit<AppShellProps, 'children'> {
  zone: { id: number | null; name: string; eventId: number | null }
  events: { id: number; name: string }[]
  errors: Record<string, string>
  urls: AppShellProps['urls'] & {
    zones: string
    createZone: string
    updateZone: string | null
    showZone: string | null
  }
  translations: AppShellProps['translations'] & {
    zones: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function New(props: Props) {
  const { zone, events, errors, urls, translations: t, ...shell } = props
  const [name, setName] = useState(zone.name || '')
  const [eventId, setEventId] = useState<number | string>(zone.eventId || '')
  const [processing, setProcessing] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(
      urls.createZone,
      { zone: { name, event_id: eventId } },
      { onFinish: () => setProcessing(false) },
    )
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title="New Zone" />

      <div className="mx-auto max-w-xl space-y-6 py-8 px-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold text-[#37352F]">New Zone</h1>
          <Button
            variant="outline"
            className="border-[#E8E8E5] text-[#37352F]"
            onClick={() => router.visit(urls.zones)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        </div>

        {/* Error summary */}
        {Object.keys(errors).length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <p className="font-medium">Please fix the following errors:</p>
            <ul className="mt-1 list-inside list-disc">
              {Object.entries(errors).map(([field, msg]) => (
                <li key={field}>{field}: {msg}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="rounded-lg border border-[#E8E8E5] p-6 space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-[#37352F]">
                Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter zone name"
                className="mt-1"
                style={{ borderColor: errors.name ? '#EF4444' : '#E8E8E5' }}
              />
              {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
            </div>

            <div>
              <Label htmlFor="event" className="text-sm font-medium text-[#37352F]">
                Event
              </Label>
              <select
                id="event"
                value={eventId}
                onChange={(e) => setEventId(Number(e.target.value))}
                className="mt-1 w-full rounded-md border px-3 py-2 text-sm text-[#37352F]"
                style={{ borderColor: errors.event_id ? '#EF4444' : '#E8E8E5' }}
              >
                <option value="">Select an event</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.name}</option>
                ))}
              </select>
              {errors.event_id && <p className="mt-1 text-xs text-red-500">{errors.event_id}</p>}
            </div>
          </div>

          <Button
            type="submit"
            disabled={processing}
            className="w-full bg-[#2383E2] hover:bg-[#1b6ec2]"
          >
            <Save className="mr-2 h-4 w-4" />
            {processing ? 'Creating...' : 'Create Zone'}
          </Button>
        </form>
      </div>
    </AppShell>
  )
}
